import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createCheckoutSession, finalizeOrder, getRegionsConfig, fetchProductPrice } from '../services/api';
import type { GeneratedProduct, GelatoCreateOrderRequest, GelatoShippingAddress, GelatoPriceEntry } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { allCountries } from '../config/countries';
import { SEO } from '../components/SEO';

export const Order = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successCreationId, setSuccessCreationId] = useState<string | null>(null);
  const [zeroDecimalCurrencies, setZeroDecimalCurrencies] = useState<string[]>([]);

  // Form State
  const [shippingAddress, setShippingAddress] = useState<GelatoShippingAddress>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postCode: '', 
    country: 'US',
    phone: '',
    email: user?.email || '',
  });
  const [quantity, setQuantity] = useState(1);
  const processingRef = useRef(false);

  // Product from navigation state or localStorage (recovery)
  const [product, setProduct] = useState<GeneratedProduct | null>(null);
  
  // Initialization
  useEffect(() => {
    // If successful, don't run initialization logic again to avoid redirecting home
    if (success) return;

    // Load regions config
    getRegionsConfig().then(config => {
      if (config.zeroDecimalCurrencies) {
        setZeroDecimalCurrencies(config.zeroDecimalCurrencies);
      }
    }).catch(console.error);

    // Force price update to USD regardless of country
    const updatePriceToUSD = async () => {
        if (!product) return;
        try {
            // Always fetch USD price
            const priceInfo = await fetchProductPrice(product.productUid, shippingAddress.country, 'USD');
            
            setProduct(prev => {
                if (!prev) return null;
                
                const newPriceEntry: GelatoPriceEntry = {
                    productUid: priceInfo.productUid,
                    country: priceInfo.country,
                    currency: priceInfo.currency,
                    price: priceInfo.unitPrice,
                    quantity: prev.productPrice?.quantity || 1,
                    pageCount: prev.productPrice?.pageCount
                };

                return {
                    ...prev,
                    productPrice: newPriceEntry,
                    currency: priceInfo.currency
                };
            });
        } catch (e) {
            console.error("Failed to fetch USD price", e);
        }
    };
    
    // Only update if not already USD or if we need to refresh
    if (product && product.productPrice?.currency !== 'USD') {
        updatePriceToUSD();
    }

    // Check if we are returning from Stripe
    if (sessionIdParam) {
      if (processingRef.current) return;
      processingRef.current = true;
      handleStripeReturn(sessionIdParam);
      return;
    }

    // Normal entry
    if (location.state?.product) {
      setProduct(location.state.product);
      // Save to local storage for persistence during redirect
      localStorage.setItem('pendingOrderProduct', JSON.stringify(location.state.product));
      if (location.state.creationId) {
        localStorage.setItem('pendingOrderCreationId', location.state.creationId);
      }
    } else {
      // Try to recover from local storage if refreshing or just back
      const stored = localStorage.getItem('pendingOrderProduct');
      if (stored) {
        setProduct(JSON.parse(stored));
      } else {
         // No product, redirect back
         navigate('/');
      }
    }
    
    // Load pending address if any
    const storedAddress = localStorage.getItem('pendingShippingAddress');
    if (storedAddress) {
        setShippingAddress(JSON.parse(storedAddress));
    }
  }, [location, sessionIdParam, success]);

  const handleStripeReturn = async (sessionId: string) => {
    setLoading(true);
    try {
      // Recover order details
      const storedProduct = localStorage.getItem('pendingOrderProduct');
      const storedAddress = localStorage.getItem('pendingShippingAddress');
      const storedQty = localStorage.getItem('pendingOrderQuantity');
      const storedCreationId = localStorage.getItem('pendingOrderCreationId');
      
      if (!storedProduct || !storedAddress) {
        throw new Error("Order details lost. Please try again.");
      }
      
      const productData = JSON.parse(storedProduct) as GeneratedProduct;
      const addressData = JSON.parse(storedAddress) as GelatoShippingAddress;
      const qty = storedQty ? parseInt(storedQty) : 1;
      
      setProduct(productData); // for display
      setShippingAddress(addressData);
      
      // Construct Order Request
      // We generate a unique reference ID
      const orderRefId = `${user?.uid}_${Date.now()}`;
      
      const orderRequest: GelatoCreateOrderRequest = {
          orderType: 'order',
          orderReferenceId: orderRefId,
          currency: productData.currency || 'USD',
          items: [{
              itemReferenceId: orderRefId,
              productUid: productData.productUid || productData.id, 
              files: [{ url: productData.previewUrl }],
              quantity: qty
          }],
          shipmentMethodUid: 'normal', 
          shippingAddress: addressData,
          metadata: [
             { key: 'user_id', value: user?.uid || '' },
             ...(storedCreationId ? [{ key: 'creation_id', value: storedCreationId }] : [])
          ]
      };
      
      await finalizeOrder(sessionId, orderRequest);
      
      if (storedCreationId) {
        setSuccessCreationId(storedCreationId);
      }
      setSuccess(true);
      // Clear storage
      localStorage.removeItem('pendingOrderProduct');
      localStorage.removeItem('pendingShippingAddress');
      localStorage.removeItem('pendingOrderQuantity');
      localStorage.removeItem('pendingOrderCreationId');
      
      // Remove query param to prevent re-submission on refresh
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('session_id');
      navigate({ search: newSearchParams.toString() }, { replace: true });
      
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Payment verification failed.");
      // Allow retry if it failed (e.g. network error)
      processingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!product || !product.productPrice) return 0;
    return product.productPrice.price * quantity;
  };

  const handlePlaceOrder = async () => {
    if (!product) return;
    
    // Validate
    if (!shippingAddress.firstName || !shippingAddress.addressLine1 || !shippingAddress.city || !shippingAddress.postCode || !shippingAddress.country || !shippingAddress.phone || !shippingAddress.email) {
        setError("Please fill in all required fields.");
        return;
    }

    if (shippingAddress.country === 'JP' && !/^\d{3}-\d{4}$/.test(shippingAddress.postCode)) {
        setError("Japan postal code must be in format xxx-xxxx");
        return;
    }

    setLoading(true);
    setError(null);
    
    try {
        // Save state for return
        localStorage.setItem('pendingOrderProduct', JSON.stringify(product));
        localStorage.setItem('pendingShippingAddress', JSON.stringify(shippingAddress));
        localStorage.setItem('pendingOrderQuantity', quantity.toString());

        // Always use USD
        const currency = 'USD';
        const isZeroDecimal = zeroDecimalCurrencies.includes(currency);
        const amountCents = isZeroDecimal 
          ? Math.round(calculateTotal()) 
          : Math.round(calculateTotal() * 100);
        const { url } = await createCheckoutSession(amountCents, currency, product.title, undefined, undefined, undefined, shippingAddress.email);
        
        if (url) {
            window.location.href = url;
        } else {
            throw new Error("Failed to start checkout.");
        }
    } catch (err: any) {
        setError(err.message || "Checkout failed");
        setLoading(false);
    }
  };
  
  // Render Success
  if (success) {
      return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
              <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2">Order Confirmed!</h1>
                  <p className="text-gray-600 mb-6">Thank you for your purchase. We will start processing your order immediately.</p>
                  
                  <div className="text-left bg-gray-50 p-4 rounded-lg mb-6 text-sm">
                    <p className="font-bold mb-2 text-gray-900">Shipping Address</p>
                    <p className="text-gray-700">{shippingAddress.firstName} {shippingAddress.lastName}</p>
                    <p className="text-gray-700">{shippingAddress.addressLine1}</p>
                    {shippingAddress.addressLine2 && <p className="text-gray-700">{shippingAddress.addressLine2}</p>}
                    <p className="text-gray-700">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postCode}</p>
                    <p className="text-gray-700">{shippingAddress.country}</p>
                    <p className="text-gray-700 mt-1">{shippingAddress.phone}</p>
                  </div>

                  <button 
                    onClick={() => navigate('/', { state: { creationId: successCreationId, openOrderDetails: true } })} 
                    className="w-full bg-black text-white py-3 rounded-lg font-medium"
                  >
                      View Order Details
                  </button>
              </div>
          </div>
      );
  }

  if (!product && !loading) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Checkout"
        description="Complete your order for your custom AI art masterpiece."
      />
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-black mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
        </button>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left: Product Info */}
            <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden mb-4">
                         <img src={product?.previewUrl} alt={product?.title} className="w-full h-full object-contain" />
                    </div>
                    <h2 className="text-xl font-bold mb-2">{product?.title}</h2>
                    <p className="text-gray-500 text-sm mb-4">{product?.description}</p>
                    <div className="flex justify-between items-center pt-4 border-t">
                        <span className="font-medium">Price</span>
                        <span className="text-xl font-bold">
                            {product?.productPrice?.currency || '$'} {product?.productPrice?.price?.toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Right: Shipping & Payment */}
            <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold mb-4">Shipping Address</h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                <input 
                                    type="text" 
                                    value={shippingAddress.firstName}
                                    onChange={e => setShippingAddress({...shippingAddress, firstName: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                <input 
                                    type="text" 
                                    value={shippingAddress.lastName}
                                    onChange={e => setShippingAddress({...shippingAddress, lastName: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                            <input 
                                type="text" 
                                value={shippingAddress.addressLine1}
                                onChange={e => setShippingAddress({...shippingAddress, addressLine1: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                            />
                        </div>
                        
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (Optional)</label>
                            <input 
                                type="text" 
                                value={shippingAddress.addressLine2}
                                onChange={e => setShippingAddress({...shippingAddress, addressLine2: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                <input 
                                    type="text" 
                                    value={shippingAddress.city}
                                    onChange={e => setShippingAddress({...shippingAddress, city: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                                <input 
                                    type="text" 
                                    value={shippingAddress.state || ''}
                                    onChange={e => setShippingAddress({...shippingAddress, state: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                                <input 
                                    type="text" 
                                    value={shippingAddress.postCode}
                                    onChange={e => setShippingAddress({...shippingAddress, postCode: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                <select 
                                    value={shippingAddress.country}
                                    onChange={e => setShippingAddress({...shippingAddress, country: e.target.value})}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none bg-white"
                                >
                                    {allCountries.map(c => (
                                        <option key={c.code} value={c.code}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <input 
                                type="tel" 
                                value={shippingAddress.phone}
                                onChange={e => setShippingAddress({...shippingAddress, phone: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                placeholder="+1 (555) 000-0000"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input 
                                type="email" 
                                value={shippingAddress.email || ''}
                                onChange={e => setShippingAddress({...shippingAddress, email: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                placeholder="your@email.com"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold mb-4">Order Summary</h3>
                    
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-600">Quantity</span>
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50"
                            >-</button>
                            <span className="font-medium w-4 text-center">{quantity}</span>
                            <button 
                                onClick={() => setQuantity(Math.min(100, quantity + 1))}
                                className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50"
                            >+</button>
                        </div>
                    </div>
                    
                    <div className="border-t pt-4 mt-4">
                        <div className="flex justify-between items-center text-lg font-bold">
                            <span>Total</span>
                            <span>{product?.productPrice?.currency || '$'} {calculateTotal().toFixed(2)}</span>
                        </div>
                    </div>
                    
                    {error && (
                        <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}
                    
                    <button 
                        onClick={handlePlaceOrder}
                        disabled={loading}
                        className="w-full mt-6 bg-black text-white py-4 rounded-xl font-medium text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                        {loading ? 'Processing...' : 'Place Order'}
                    </button>
                    <p className="text-center text-xs text-gray-400 mt-3">
                        Secure payment processing by Stripe
                    </p>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
